<template>
  <article v-if="data" class="mb-[100px] flex flex-col md:mb-[120px]">
    <BaseTheContentBreadcrumbs
      :current-category="data.category"
      :current-page-title="data.title"
    />
    <div
      class="mb-[36px] grid grid-cols-9 gap-[20px] md:mb-[56px] xl:grid-cols-8 xl:gap-[28px]"
    >
      <h1
        class="col-span-full text-[18px] font-medium leading-[24px] md:col-span-7 lg:text-[20px] lg:leading-[26px] xl:text-[32px] xl:leading-[38px]"
        v-text="data.title"
      />
      <SlidersDocumentsSlider
        v-if="data.images?.length"
        class="col-span-4 md:col-span-2"
        :slides="data.images"
        @on-slide-change="currentSlideIndex = $event"
        @on-swiper="onSwiper"
      />
      <div class="page-body col-span-full md:col-span-5" v-html="data.body" />
      <div
        class="col-span-4 col-start-5 row-start-2 md:col-span-2 md:col-start-8 md:row-span-full md:row-start-auto xl:col-span-1 xl:col-start-8"
      >
        <div
          v-if="data.images?.length > 1"
          class="flex w-min items-baseline rounded-[12px] p-[12px] shadow-drop-0 2xl:px-[20px]"
        >
          <SlidersSliderControl
            type="prev"
            class="documents-slider-prev mr-[8px]"
            :class="isBeginning ? 'opacity-50' : 'cursor-pointer'"
            @click="slidePrev()"
          />
          <SlidersSliderControl
            type="next"
            class="documents-slider-next 3xl:mr-[24px]"
            :class="isEnd ? 'opacity-50' : 'cursor-pointer'"
            @click="slideNext()"
          />
          <span
            class="hidden whitespace-nowrap text-[18px] font-medium leading-[22px] 3xl:inline-block"
            >{{ currentSlideIndex + 1 }} / {{ data.images.length }}</span
          >
        </div>
      </div>
    </div>

    <h2
      class="mb-[28px] text-[16px] font-medium leading-[26px] lg:text-[24px] lg:leading-[34px]"
    >
      Содержание документа
    </h2>

    <div
      class="mb-[36px] grid grid-cols-2 gap-[8px] md:mb-[64px] md:grid-cols-5 xl:grid-cols-8 xl:gap-[20px]"
    >
      <div
        v-for="(image, index) in data.images"
        :key="index"
        class="col-span-1 flex max-h-[234px] max-w-[174px] cursor-pointer"
      >
        <img
          v-lazy-load
          :src="image.preview"
          alt=""
          class="object-cover"
          @click="showLightbox(index)"
        />
      </div>
    </div>

    <BaseTheButtonNew
      class="self-center"
      text="Скачать PDF"
      :href="data.file"
      is-external
    />
  </article>
</template>

<script setup>
import { Fancybox } from 'fancyapps-ui';

definePageMeta({
  layout: 'marginless',
});

const route = useRoute();
const category = route?.params?.slug;
const slug = route?.params?.document;

const { onSwiper, slideNext, slidePrev, isBeginning, isEnd } = useSwiper();

const currentSlideIndex = ref(0);
const robots = ref('all');

const { documentSlug } = useDocumentBySlug(category, slug);
useErrorHandler(() => documentSlug.failureReason.value?.status);

const data = computed(() => documentSlug?.data?.value);

const showLightbox = (index) => {
  const images = data.value.images.map((el) => {
    return {
      src: el.src ?? el.preview,
      thumbSrc: el.preview,
    };
  });
  Fancybox.show(images, {
    startIndex: index,
    compact: false,
    contentClick: 'iterateZoom',
    Images: {
      Panzoom: {
        maxScale: 2,
      },
    },
    Thumbs: {
      type: 'classic',
      showOnStart: false,
      thumbTpl:
        '<button type="button" aria-label="{{GOTO}}"><img class="f-thumb" data-not-lazy data-lazy-src="{{%s}}" alt="" /></button>',
    },
  });
};

const seo = computed(() => data.value?.seo);

watch(seo, (value) => {
  if (value) {
    const arr = [];
    if (value.index === false) arr.push('noindex');
    if (value.follow === false) arr.push('nofollow');
    if (value.archive === false) arr.push('noarchive');
    if (arr.length > 0) {
      robots.value = arr.reduce((previousValue, currentValue) => {
        return `${previousValue}, ${currentValue}`;
      });
    }
  }
});

useHead(
  computed(() => ({
    title: `${
      data.value?.title ? `${data.value?.title} | ` : ''
    } Читать исторические документы РФ для школьников и студентов`,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: `${
          seo.value?.metadescription ?? data.value?.title
        }. Читать исторические документы РФ для школьников и студентов на сайте Федерального портала истории России.`,
      },
      {
        'http-equiv': 'Last-Modified',
        content: new Date(seo.value?.last_modified).toUTCString(),
      },
      {
        property: 'og:image',
        content: `${useHostApp()}${
          data.value?.images?.[0]?.src ?? data.value?.images?.[0]?.preview
        }`,
      },
      {
        hid: 'og:type',
        property: 'og:type',
        content: 'article',
      },
      {
        property: 'og:title',
        content: `${
          data.value?.title ? `${data.value?.title} | ` : ''
        } Читать исторические документы РФ для школьников и студентов`,
      },
      {
        property: 'og:url',
        content: `${useHostApp()}/read/documents/${category}/${slug}`,
      },
      {
        property: 'og:description',
        content: `${
          seo.value?.metadescription ?? data.value?.title
        }. Читать исторические документы РФ для школьников и студентов на сайте Федерального портала истории России.`,
      },
      {
        hid: 'robots',
        name: 'robots',
        content: robots.value,
      },
    ],
    link: [
      {
        rel: 'canonical',
        href: `${useHostApp()}${route.path}`,
      },
    ],
  })),
);
</script>
